import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { WhatsAppButton } from './WhatsAppButton';
import {
  Clock,
  Droplets,
  Heart,
  Activity,
  Gauge,
  HeartPulse,
  Waves,
  Sun,
  Sparkles,
  TrendingUp,
  Beaker,
  Bug,
  Shield,
  ArrowRight
} from 'lucide-react';

const iconMap = {
  Droplets,
  Heart,
  Activity,
  Gauge,
  HeartPulse,
  Waves,
  Sun,
  Sparkles,
  TrendingUp,
  Beaker,
  Bug,
  Shield
};

export const TestCard = ({ test, showFullDetails = false }) => {
  const IconComponent = iconMap[test.icon] || Beaker;

  return (
    <Card className="h-full flex flex-col hover:shadow-card-hover transition-all duration-300 group">
      <CardContent className="pt-6 pb-3 flex-1 flex flex-col">
        {/* Icon and Badge */}
        <div className="flex items-start justify-between mb-4">
          <div className="p-3 bg-primary/10 rounded-xl group-hover:bg-primary/15 transition-all duration-300">
            <IconComponent className="h-6 w-6 text-primary" />
          </div>
          {test.popular && (
            <Badge variant="secondary" className="text-xs font-medium px-2 py-0.5">
              Popular
            </Badge>
          )}
        </div>

        {/* Test Name */}
        <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-300 leading-tight">
          {test.name}
        </h3>

        {/* Short Description */}
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed flex-1 min-h-[3rem]">
          {test.shortDescription}
        </p>

        {/* Test Details */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <Clock className="h-3.5 w-3.5 text-primary flex-shrink-0" />
            <span className="truncate">{test.reportTime}</span>
          </div>
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <Droplets className="h-3.5 w-3.5 text-primary flex-shrink-0" />
            <span className="truncate">{test.sample}</span>
          </div>
        </div>

        {/* Price */}
        <div className="mb-4">
          <span className="text-2xl font-bold text-primary block leading-none">{test.price}</span>
          <span className="text-xs text-muted-foreground">onwards</span>
        </div>
      </CardContent>

      {/* Optimized Mobile-First Footer */}
      <CardFooter className="pt-2 pb-4 px-4 sm:px-6 !border-t !border-border/50 bg-gradient-to-b from-transparent to-background/80">
        <div className="w-full grid grid-cols-1 gap-2 sm:flex sm:flex-row sm:gap-2">
          <WhatsAppButton
            testName={test.name}
            className="!w-full sm:!flex-1 h-10 text-sm font-medium"
            size="sm"
          />
          <Button
            asChild
            variant="outline"
            size="sm"
            className="!w-full sm:!flex-1 h-10 text-sm font-medium border-2 hover:border-primary/80 transition-all duration-300 justify-center items-center gap-1.5 px-3 py-0"
          >
            <Link to={`/tests/${test.id}`} className="flex items-center gap-1.5">
              Details
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-300" />
            </Link>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};
